import { Alert } from '@/components/ui/alert';
import { LoadingPage } from '@/components/ui/loading-page';
import { useAuth } from '@/contexts/auth-context';
import { useMcpDecisionAction } from '@/hooks/actions/use-mcp-actions';
import { useMcpConsentQuery } from '@/hooks/queries/use-mcp-query';
import { isApiErrorCode } from '@/libs/api-error';
import { getMcpReturnTo } from '@/libs/mcp-return-to';
import { Box, Button, Flex, Stack, Text } from '@chakra-ui/react';
import { ErrorCode, upgradeTierFor, PlanTier, type PlanLimitReachedData } from '@fluenceur/types';
import { Logo } from '@fluenceur/ui';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { UpgradeModal } from '@/components/subscription/UpgradeModal';
import { useBillingContextQuery } from '@/hooks/queries/use-billing-context-query';
import { isPlanLimitError, planLimitOfCode, readPlanLimitRefusal } from '@/libs/plan-limit-refusal';
import { usePaywall } from '@/hooks/use-paywall';
import { isAxiosError } from 'axios';
import { useEffect, useMemo, useRef, useState } from 'react';
import { McpConsent } from './McpConsent';

/**
 * Keep the OAuth transaction underneath its typed paywall. The refusal is
 * scoped by the common host; its free way out leads to the dashboard.
 */
function PlanLockedConsent({ planLimit }: { planLimit: PlanLimitReachedData }) {
  const navigate = useNavigate();
  const { openPlanLimitModal } = usePaywall();
  const openedRefusal = useRef<PlanLimitReachedData | null>(null);
  useEffect(() => {
    if (openedRefusal.current === planLimit) return;
    openedRefusal.current = planLimit;
    openPlanLimitModal(planLimit, { onClose: () => navigate(DASHBOARD_PATH) });
  }, [planLimit, openPlanLimitModal, navigate]);
  return null;
}

const DASHBOARD_PATH = '/dashboard';

/** The plan limit a refusal names, read from its payload or else from its code. */
function planLimitOf(error: unknown, planTier: PlanTier): PlanLimitReachedData | null {
  const body = isAxiosError(error) ? error.response?.data : undefined;
  if (!isPlanLimitError(error)) return null;
  const planLimit = readPlanLimitRefusal(body);
  if (planLimit) return planLimit;
  const limit = planLimitOfCode(body);
  return limit
    ? { limit, currentTier: planTier, targetTier: upgradeTierFor(limit, planTier) }
    : null;
}

function ConsentTransaction({ transaction }: { transaction: string }) {
  const { t } = useTranslation();
  const query = useMcpConsentQuery(transaction);
  const decision = useMcpDecisionAction(transaction);
  const { data: billingContext } = useBillingContextQuery();
  const planTier = billingContext?.effectiveTier ?? PlanTier.FREE;
  const [decisionRefusal, setDecisionRefusal] = useState<PlanLimitReachedData | null>(null);
  const queryRefusal = useMemo(() => planLimitOf(query.error, planTier), [query.error, planTier]);
  if (query.isLoading) return <LoadingPage />;
  const planLimit = decisionRefusal ?? queryRefusal;
  if (planLimit) return <PlanLockedConsent planLimit={planLimit} />;
  if (query.isError || !query.data)
    return (
      <Stack gap={4}>
        {isApiErrorCode(query.error, ErrorCode.E_MCP_ORGANIZATION_CONTEXT_REQUIRED) ? (
          <Alert status="warning" title={t('mcp.consent.errors.organizationContextRequired')} />
        ) : isApiErrorCode(query.error, ErrorCode.E_MCP_TEMPORARILY_UNAVAILABLE) ? (
          <Alert status="warning" title={t('mcp.temporarilyUnavailable')} />
        ) : (
          <Alert status="error" title={t('mcp.consent.unavailable')}>
            {t('mcp.consent.restart')}
          </Alert>
        )}
        <Button variant="outline" onClick={() => void query.refetch()}>
          {t('mcp.retry')}
        </Button>
      </Stack>
    );
  return (
    <McpConsent
      key={query.data.transactionId}
      consent={query.data}
      onDecision={async (payload) => {
        let redirectUrl: string;
        try {
          ({ redirectUrl } = await decision.mutateAsync(payload));
        } catch (error) {
          const refusal = planLimitOf(error, planTier);
          if (refusal) setDecisionRefusal(refusal);
          throw error;
        }
        // The server returns only the OAuth client's prevalidated exact callback.
        window.location.assign(redirectUrl);
      }}
    />
  );
}

export default function McpAuthorizePage() {
  const { t } = useTranslation();
  const { isAuthenticated, isLoading } = useAuth();
  const [params] = useSearchParams();
  const transaction = params.get('transaction');
  const returnTo = getMcpReturnTo(`/mcp/authorize?${params.toString()}`);
  if (isLoading) return <LoadingPage />;
  if (returnTo && !isAuthenticated)
    return <Navigate to={`/?${new URLSearchParams({ returnTo }).toString()}`} replace />;
  return (
    <Box minH="100vh" bg="bg.subtle" px={4} py={{ base: 6, md: 12 }}>
      <UpgradeModal />
      <Flex maxW="600px" mx="auto" mb={6} justify="center">
        <Logo width="143px" height="24px" />
      </Flex>
      <Box
        maxW="600px"
        mx="auto"
        bg="bg"
        borderWidth="1px"
        borderRadius="2xl"
        p={{ base: 5, md: 8 }}
      >
        {returnTo && transaction ? (
          <ConsentTransaction transaction={transaction} />
        ) : (
          <Stack>
            <Alert status="error" title={t('mcp.consent.unavailable')} />
            <Text>{t('mcp.consent.restart')}</Text>
          </Stack>
        )}
      </Box>
    </Box>
  );
}
